import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Row, Col } from "deltoid/core/Blocks/BootstrapContainers";
import Form from "deltoid/core/Form";
import { useForm, Controller, ErrorMessage } from "react-hook-form";
import { uiActions, dialogActions, manualUploadActions, toastActions } from "@store/ducks";
import Uploader from "./Uploader";
import { INDIVIDUAL_DOC_UPLOAD_STATUS } from "@constants/manualUploadConstants";
import SingleFileUploader from "./SingleFileUploader";

const UploadItem = ({
  category,
  categoryLabel,
  subCategories,
  multiple,
  fileCount,
  docTypes,
  maxFileSizeLimit,
  openFrontAndBackUploadDialog,
  openMultipleFileUploadDialog,
  removeFile,
  loadUI,
  downloadSigningAuthorityLetter,
  showToast,
}) => {
  const [downloading, setDownloading] = useState(false);
  const { errors, setValue, getValues, control } = useForm({
    mode: "onBlur",
  });

  useEffect(() => {
    setUploadedFile(category?.kyc_document);
    setSubCategoryLabel(category.kyc_document ?
      `Uploaded ${category.kyc_document.document_type_name} Proof` : "Upload Document");
    if (category?.kyc_document) {
      const docTypeOption = [subCategories.find((v) => v.id === category?.kyc_document?.document_type_id)];
      if (docTypeOption[0]) {
        setValue("documentType", docTypeOption[0]);
        setSelectedSubCategoryName(docTypeOption[0]?.name_on_frontend);
        if (!(category?.kyc_document?.document_type_id == docTypeOption[0]?.id)) {
          setResetSingleFileUploader({ update: true, value: null });
        } else {
          setResetSingleFileUploader({ update: true, value: category?.kyc_document?.processed_document });
        }
      }
    }
    else if (subCategories.length === 1) {
      setValue("documentType", subCategories[0]);
      setSelectedSubCategoryName(subCategories[0]?.name_on_frontend);
      if (!(category?.kyc_document?.document_type_id == subCategories[0]?.id)) {
        setResetSingleFileUploader({ update: true, value: null });
      } else {
        setResetSingleFileUploader({ update: true, value: category?.kyc_document?.processed_document });
      }
    }
  }, [category]);

  const [uploadedFile, setUploadedFile] = useState(category?.kyc_document);

  const [subCategoryLabel, setSubCategoryLabel] = useState(
    category.kyc_document ? `Uploaded ${category.kyc_document.document_type_name} Proof` : "Upload Document"
  );

  const [selectedSubCategoryName, setSelectedSubCategoryName] = useState("");
  const [resetSingleFileUploader, setResetSingleFileUploader] = useState({ update: false, value: null });

  const onDocTypeSelected = ([inputValue]) => {
    const label = (subCategories && subCategories.find((type) => type.id === inputValue.id)).name || inputValue.label;
    setSubCategoryLabel(`Upload ${label}`);
    setSelectedSubCategoryName(inputValue.name_on_frontend);
    if (!(uploadedFile && uploadedFile.document_type_id == inputValue.id)) {
      setResetSingleFileUploader({ update: true, value: null });
    } else {
      setResetSingleFileUploader({ update: true, value: uploadedFile.processed_document });
    }
    return inputValue;
  };

  const handleUploadedFile = (file) => {
    setUploadedFile(file);
  };

  const onFileRemove = () => {
    setUploadedFile(null);
  };

  const onMultipleFileRemove = (callback) => {
    const docUUID = uploadedFile.uuid;
    removeFile(docUUID, (response) => {
      if (response) {
        setUploadedFile(null);
        callback("initial");
      } else {
        callback("uploaded");
      }
    });
  };

  const openFrontAndBackDialog = () => {
    const documentDetails = docTypes.find((docType) => docType.name === selectedSubCategoryName);
    const docTypeDetails = category.document_types.find(
      (document_type) => document_type.name_on_frontend === selectedSubCategoryName
    );
    const props = {
      modalTitle: "Upload " + category.name,
      modalSubTitle: "Upload front and back side of " + docTypeDetails.name,
      documentCategoryId: category.id,
      documentTypeId: docTypeDetails.id,
      frontDocName: documentDetails.frontDocName,
      backDocName: documentDetails.backDocName,
      optional: documentDetails.optional,
      maxFileSizeLimit: maxFileSizeLimit,
      uploadedDocument: category.kyc_document
        ? getValues("documentType").id == category.kyc_document.document_type_id ||
          getValues("documentType").id == undefined
          ? category.kyc_document
          : null
        : null,
      setUploadedFile: handleUploadedFile,
    };
    openFrontAndBackUploadDialog(props);
  };

  const openMultipleFileDialog = () => {
    const docTypeDetails = category.document_types.find(
      (document_type) => document_type.name_on_frontend === selectedSubCategoryName
    );
    const props = {
      modalTitle: "Upload " + category.name,
      documentCategoryId: category.id,
      documentTypeId: docTypeDetails.id,
      maxFileSizeLimit: maxFileSizeLimit,
      uploadedDocument: category.kyc_document
        ? getValues("documentType").id == category.kyc_document.document_type_id ||
          getValues("documentType").id == undefined
          ? category.kyc_document
          : null
        : null,
      setUploadedFile: handleUploadedFile,
    };
    openMultipleFileUploadDialog(props);
  };

  const downloadSampleSigningAuthorityLetter = () => {
    setDownloading(true);
    downloadSigningAuthorityLetter((kyc_document) => {
      setDownloading(false);
      if (kyc_document?.processed_document?.path) {
        window.open(
          `${window?.location?.origin}${kyc_document?.processed_document?.path}`, '_blank');
      } else {
        showToast({ variant: 'error', title: 'Error', content: 'We encountered an error. Refresh the page and try again.' });
      }
    });
  }

  return (
    <Row>
      <Col md={5}>
        <Form.Group className="group-document-type">
          <Form.Label id="documentType">{categoryLabel}</Form.Label>
          <Controller
            as={Form.Select}
            name="documentType"
            id="documentType"
            control={control}
            rules={{ required: "Please Select a Document" }}
            isMulti={false}
            placeholder="Select a Document"
            options={subCategories}
            onChange={onDocTypeSelected}
            isDisabled={uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_VERIFIED}
            value={getValues("documentType")}
          />
          {
            category?.name_on_frontend == "SIGNED_AUTHORISATION_LETTER" &&
            <Form.Button type="button" variant='underlined' loading={downloading} disabled={downloading} onClick={() => downloadSampleSigningAuthorityLetter()} className="p-0 mt-1">
              Download Sample Format
            </Form.Button>
          }
          {
            uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED &&
            <Form.Message className="sf-error">{uploadedFile && uploadedFile.error}</Form.Message >
          }
          <ErrorMessage errors={errors} name="documentType">
            {({ message }) => <Form.Message type="error">{message}</Form.Message>}
          </ErrorMessage>
        </Form.Group>
      </Col>
      <Col md={5} className="offset-md-1">
        <Form.Group>
          <Form.Label>{subCategoryLabel}</Form.Label>
          {multiple ? (
            fileCount == "uploadFrontAndBack" &&
              docTypes.find((docType) => docType.name === selectedSubCategoryName) ? (
              <>
                <SingleFileUploader
                  onFileDropAccepted={() => { }}
                  onFileDropRejected={() => { }}
                  allowedFileExtensions="image/png, application/pdf, image/jpg, image/jpeg"
                  maxFileSizeInMB="30"
                  onMultipleRemove={onMultipleFileRemove}
                  disabled={true}
                  multiple={false}
                  uploadedDocument={uploadedFile ? uploadedFile.processed_document : null}
                  uploaderState={
                    uploadedFile && uploadedFile.processed_document
                      ? uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED
                        ? "uploadError"
                        : "uploaded"
                      : "initial"
                  }
                  onUploadIconClick={() => {
                    return uploadedFile &&
                      uploadedFile.processed_document &&
                      (uploadedFile.document_type_id == getValues("documentType").id ||
                        getValues("documentType").id == undefined) ||
                      uploadedFile?.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_VERIFIED
                      ? null
                      : openFrontAndBackDialog();
                  }}
                  fileCount={fileCount}
                  resetSingleFileUploader={resetSingleFileUploader}
                  kycDocumentStatus={uploadedFile ? uploadedFile.status : null}
                />
                {
                  uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED &&
                  <Form.Message className="sf-remarks">{uploadedFile && uploadedFile.remarks}</Form.Message>
                }
                {
                  uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_VERIFIED &&
                  <Form.Message className="sf-remarks">This document has been approved.</Form.Message>
                }
              </>
            ) : fileCount == "multiple" && docTypes.find((docType) => docType === selectedSubCategoryName) ? (
              <>
                <SingleFileUploader
                  onFileDropAccepted={() => { }}
                  onFileDropRejected={() => { }}
                  allowedFileExtensions="image/png, application/pdf, image/jpg, image/jpeg"
                  maxFileSizeInMB="30"
                  onMultipleRemove={onMultipleFileRemove}
                  disabled={true}
                  multiple={false}
                  uploadedDocument={uploadedFile ? uploadedFile.processed_document : null}
                  uploaderState={
                    uploadedFile && uploadedFile.processed_document
                      ? uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED
                        ? "uploadError"
                        : "uploaded"
                      : "initial"
                  }
                  onUploadIconClick={() => {
                    return uploadedFile &&
                      uploadedFile.processed_document &&
                      (uploadedFile.document_type_id == getValues("documentType").id ||
                        getValues("documentType").id == undefined) ||
                      uploadedFile?.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_VERIFIED
                      ? null
                      : openMultipleFileDialog();
                  }}
                  fileCount={fileCount}
                  resetSingleFileUploader={resetSingleFileUploader}
                  kycDocumentStatus={uploadedFile ? uploadedFile.status : null}
                />
                {
                  uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED &&
                  <Form.Message className="sf-remarks">{uploadedFile && uploadedFile.remarks}</Form.Message>
                }
                {
                  uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_VERIFIED &&
                  <Form.Message className="sf-remarks">This document has been approved.</Form.Message>
                }
              </>
            ) : (
              <>
                <Uploader
                  documentCategoryId={category.id}
                  documentTypeId={
                    uploadedFile &&
                      getValues("documentType") &&
                      uploadedFile.document_type_id &&
                      uploadedFile.document_type_id == getValues("documentType").id
                      ? uploadedFile.document_type_id
                      : getValues("documentType")
                        ? getValues("documentType").id
                        : null
                  }
                  maxFileSizeLimit={maxFileSizeLimit}
                  uploadedDocument={uploadedFile ? uploadedFile.processed_document : null}
                  uploadedDocumentStatus={
                    uploadedFile && uploadedFile.processed_document
                      ? uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED
                        ? "uploadError"
                        : "uploaded"
                      : "initial"
                  }
                  multiple={false}
                  onFileRemove={onFileRemove}
                  onUploadIconClick={null}
                  setUploadedFile={handleUploadedFile}
                  resetSingleFileUploader={resetSingleFileUploader}
                  kycDocumentStatus={uploadedFile ? uploadedFile.status : null}
                />
                {
                  uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED &&
                  <Form.Message className="sf-remarks">{uploadedFile && uploadedFile.remarks}</Form.Message>
                }
                {
                  uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_VERIFIED &&
                  <Form.Message className="sf-remarks">This document has been approved.</Form.Message>
                }
              </>
            )
          ) : (
            <>
              <Uploader
                documentCategoryId={category.id}
                documentTypeId={
                  uploadedFile &&
                    getValues("documentType") &&
                    uploadedFile.document_type_id &&
                    uploadedFile.document_type_id == getValues("documentType").id
                    ? uploadedFile.document_type_id
                    : getValues("documentType")
                      ? getValues("documentType").id
                      : null
                }
                maxFileSizeLimit={maxFileSizeLimit}
                uploadedDocument={uploadedFile ? uploadedFile.processed_document : null}
                uploadedDocumentStatus={
                  uploadedFile && uploadedFile.processed_document
                    ? uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED
                      ? "uploadError"
                      : "uploaded"
                    : "initial"
                }
                multiple={false}
                onFileRemove={onFileRemove}
                onUploadIconClick={null}
                setUploadedFile={handleUploadedFile}
                resetSingleFileUploader={resetSingleFileUploader}
                kycDocumentStatus={uploadedFile ? uploadedFile.status : null}
              />
              {
                uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_REJECTED &&
                <Form.Message className="od-message sf-remarks">{uploadedFile && uploadedFile.remarks}</Form.Message>
              }
              {
                uploadedFile && uploadedFile.status === INDIVIDUAL_DOC_UPLOAD_STATUS.DOCUMENT_VERIFIED &&
                <Form.Message className="od-message sf-remarks">This document has been approved.</Form.Message>
              }
            </>
          )}
        </Form.Group>
      </Col>
    </Row>
  );
};

const mapDispatchToProps = (dispatch) => ({
  openFrontAndBackUploadDialog: (props) => dispatch(dialogActions.open("two-file-upload", props)),
  openMultipleFileUploadDialog: (props) => dispatch(dialogActions.open("multi-file-upload", props)),
  removeFile: (docUUID, callback) => dispatch(manualUploadActions.removeFile(docUUID, callback)),
  loadUI: () => dispatch(uiActions.loadUI({})),
  downloadSigningAuthorityLetter: (callback) => dispatch(manualUploadActions.downloadSigningAuthorityLetter(callback)),
  showToast: toastProps => dispatch(toastActions.show(toastProps)),
});

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(UploadItem);
